import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, type ReactNode, useState } from "react";

import { useCheckIfModuleDisabled } from "@/hooks/useCheckIfModuleDisabled";
import { useLayoutContext } from "@/layouts/LayoutContext";
import { useTenant } from "@/user/services/tenant";

import { fetchMoodsQueryOptions, mood } from "../services";
import MoodForm from "./MoodForm";
import MoodPopup from "./MoodPopup";

export default function MoodWrapper({ children }: { children: ReactNode }) {
  const active = useCheckIfModuleDisabled("mood");
  const { hideWidgets } = useLayoutContext();
  const tenant = useTenant();
  const queryClient = useQueryClient();
  const [popupDismissed, setPopupDismissed] = useState(false);
  const shouldShowPopup = tenant.settings.mood?.should_show_popup === true;
  const canShowMood = active && !hideWidgets;
  const { data: moods, isFetched } = useQuery({
    ...fetchMoodsQueryOptions(),
    enabled: canShowMood,
  });
  const hasMoods = (moods?.length ?? 0) > 0;
  const popupPending = shouldShowPopup && !popupDismissed && !isFetched;
  const showPopup =
    canShowMood && shouldShowPopup && !popupDismissed && hasMoods;
  const showFloating = canShowMood && hasMoods && !showPopup && !popupPending;

  const closePopup = () => {
    setPopupDismissed(true);
    queryClient.invalidateQueries({ queryKey: mood.all.queryKey });
  };

  return (
    <Fragment>
      {showPopup && (
        <MoodPopup onDismiss={closePopup} onSubmitted={closePopup} />
      )}
      {showFloating && <MoodForm />}
      {children}
    </Fragment>
  );
}
